import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import "./ExcelPreview.css";

const PREVIEW_MAX_ROWS = 250;
const PREVIEW_MAX_COLS = 40;

function clipSheet(sheet) {
  if (!sheet || !sheet["!ref"]) return { sheet, truncated: false };
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const lastRow = Math.min(range.e.r, range.s.r + PREVIEW_MAX_ROWS - 1);
  const lastCol = Math.min(range.e.c, range.s.c + PREVIEW_MAX_COLS - 1);
  const truncated = lastRow < range.e.r || lastCol < range.e.c;
  if (!truncated) return { sheet, truncated: false };
  const clipped = { ...sheet };
  clipped["!ref"] = XLSX.utils.encode_range({
    s: range.s,
    e: { r: lastRow, c: lastCol },
  });
  return { sheet: clipped, truncated: true };
}

export function parseExcelWorkbook(arrayBuffer) {
  const data = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
  const workbook = XLSX.read(data, { type: "array", cellDates: true });
  if (!workbook || !Array.isArray(workbook.SheetNames) || workbook.SheetNames.length === 0) {
    return [];
  }
  return workbook.SheetNames.map((name) => {
    const { sheet, truncated } = clipSheet(workbook.Sheets[name] || {});
    const html = XLSX.utils.sheet_to_html(sheet, { editable: false });
    return {
      name,
      html: DOMPurify.sanitize(html || ""),
      truncated,
    };
  }).filter((s) => s.html);
}

function ExcelPreview({
  src,
  fileName = "",
  accentColor = "#1d6f42",
  compact = false,
  loadingLabel = "Loading",
  failedLabel = "No preview available",
  truncatedLabel = "",
  onDownload,
  downloadLabel,
}) {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(!!src);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) {
      setLoading(false);
      setFailed(true);
      setSheets([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setSheets([]);
    setActiveSheet(0);

    fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error(`Excel preview fetch failed (${response.status})`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        const parsed = parseExcelWorkbook(buffer);
        if (!parsed.length) throw new Error("Empty workbook");
        setSheets(parsed);
        setActiveSheet(0);
      })
      .catch((error) => {
        console.error("Excel preview error:", error);
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (loading) {
    return <div className="doc-preview-loading">{loadingLabel}</div>;
  }

  if (failed || sheets.length === 0) {
    return (
      <div className="excel-preview-fallback">
        <div className="doc-preview-loading">{failedLabel}</div>
        {onDownload && downloadLabel ? (
          <button
            type="button"
            className="kj-btn kj-btn--primary kj-btn--pill doc-no-preview-btn excel-preview__fallback-btn"
            onClick={onDownload}
            style={{ "--kj-tone": accentColor }}
          >
            <span className="material-symbols-outlined">
              download
            </span>
            {downloadLabel}
          </button>
        ) : null}
      </div>
    );
  }

  const current = sheets[activeSheet] || sheets[0];

  return (
    <div className={`excel-preview${compact ? " excel-preview--compact" : ""}`} style={{ "--excel-preview-accent": accentColor }}>
      <div className="excel-preview-toolbar">
        {fileName ? (
          <div className="excel-preview-filename" title={fileName}>
            <span className="material-symbols-outlined excel-preview__file-icon">
              table_chart
            </span>
            <span>{fileName}</span>
          </div>
        ) : (
          <div />
        )}
        {onDownload && downloadLabel ? (
          <button
            type="button"
            className="kj-btn kj-btn--primary kj-btn--sm kj-btn--pill excel-preview-download"
            onClick={onDownload}
            style={{ "--kj-tone": accentColor }}
          >
            <span className="material-symbols-outlined excel-preview__download-icon">
              download
            </span>
            {downloadLabel}
          </button>
        ) : null}
      </div>
      {sheets.length > 1 ? (
        <div className="excel-preview-sheet-tabs" role="tablist">
          {sheets.map((sheet, index) => (
            <button
              key={`${sheet.name}-${index}`}
              type="button"
              role="tab"
              aria-selected={index === activeSheet}
              className={`excel-preview-sheet-tab${index === activeSheet ? " is-active" : ""}`}
              onClick={() => setActiveSheet(index)}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      ) : null}
      <div
        className="excel-preview-body attachment-preview-excel"
        dangerouslySetInnerHTML={{ __html: current.html }}
      />
      {current.truncated && truncatedLabel ? (
        <div className="excel-preview-truncated">{truncatedLabel}</div>
      ) : null}
    </div>
  );
}

export default ExcelPreview;
